import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// ─── Types ───────────────────────────────────────────────
type ScanResult = {
  success: boolean;
  verified?: boolean;
  tag?: any;
  data?: any;
  product?: any;
  business?: any;
  message?: string;
};

type Status = "loading" | "authentic" | "fake" | "error";

// ─── resolveTagId (same as ScanConsumerPage) ─────────────
function resolveTagId(input: string): string {
  if (!input) return input;
  const raw = input.trim();
  if (!raw.includes("://") && !raw.startsWith("www.")) {
    return raw.replace(/^UID:/i, "");
  }
  try {
    const url = new URL(raw.startsWith("http") ? raw : "https://" + raw);
    const path = url.pathname;
    const q = url.searchParams.get("tagId") || url.searchParams.get("UID") || url.searchParams.get("uid");
    if (q) return q.replace(/^UID:/i, "");
    const uid = path.match(/\/uid\/(?:UID:)?([^\/\?]+)/i);
    if (uid) return uid[1];
    const gs1 = path.match(/\/01\/\d+\/21\/([^\/\?]+)/);
    if (gs1) return gs1[1];
    const scan = path.match(/\/(?:scan|verify)\/([^\/\?]+)/);
    if (scan) return scan[1];
    const parts = path.split("/").filter(Boolean);
    return parts[parts.length - 1]?.replace(/^UID:/i, "") || raw;
  } catch {
    return raw;
  }
}

const APP_STORE_URL = "https://apps.apple.com/us/app/jeptags/id1367339677";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.jeptags"; // ← update when Android published

const AuthenticScanPage: React.FC = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const rawId =
    searchParams.get("tagId") || searchParams.get("UID") || searchParams.get("uid") ||
    params.tagId || params.uid || params.gtin || "";

  const tagId = resolveTagId(rawId);

  const [status, setStatus] = useState<Status>("loading");
  const [scanData, setScanData] = useState<ScanResult | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const cancelRef = useRef(false);

  const baseUrl =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? `${window.location.protocol}//${window.location.hostname}:3000/api/scanConsumer`
      : `${window.location.origin}/api/scanConsumer`;

  useEffect(() => {
    if (!tagId) { setStatus("error"); return; }
    cancelRef.current = false;

    const doScan = async () => {
      try {
        const res = await fetch(`${baseUrl}/level1/${encodeURIComponent(tagId)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deviceInfo: { userAgent: navigator.userAgent, platform: navigator.platform, scanType: "url" }
          })
        });
        if (cancelRef.current) return;
        const raw = await res.json();
        if (cancelRef.current) return;

        setScanData(raw);

        // Extract scan count
        const tag = raw.tag || raw.data;
        const scans = tag?.stats?.totalScans ?? tag?.security?.totalScans ?? raw.data?.stats?.totalScans ?? 0;
        setScanCount(scans);

        if (raw.success && (raw.verified !== false)) {
          setStatus("authentic");
        } else {
          setStatus("fake");
        }
      } catch {
        if (!cancelRef.current) setStatus("error");
      }
    };

    const t = setTimeout(doScan, 300);
    return () => { cancelRef.current = true; clearTimeout(t); };
  }, [tagId]);

  // Extract product info from either response format
  const tag = scanData?.tag || scanData?.data || {};
  const product = tag?.product || scanData?.product || {};
  const business = tag?.business || scanData?.business || {};
  const productName = product?.name || tag?.productName || "Verified Product";
  const productBrand = product?.brand || business?.companyName || business?.tradingName || "";
  const productImage = product?.images?.[0]?.url || product?.image || product?.thumbnail || "";
  const productCategory = product?.category || "";
  const batchId = tag?.traceability?.batchId || product?.batchId || "";

  return (
    <div style={{
      minHeight: "100vh",
      background: "#090e1a",
      fontFamily: "'DM Sans', 'Outfit', system-ui, sans-serif",
      color: "#fff",
      overflowX: "hidden",
      position: "relative"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Bebas+Neue&family=Syne:wght@700;800&display=swap');

        @keyframes pulse-ring {
          0%   { transform: scale(0.8); opacity: 0.8; }
          50%  { transform: scale(1.15); opacity: 0.3; }
          100% { transform: scale(0.8); opacity: 0.8; }
        }
        @keyframes shield-in {
          0%   { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          60%  { transform: scale(1.1) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes check-draw {
          0%   { stroke-dashoffset: 100; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes fade-up {
          0%   { transform: translateY(24px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 30px rgba(0,255,180,0.15); }
          50%       { box-shadow: 0 0 60px rgba(0,255,180,0.35); }
        }
        @keyframes bg-shift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes shimmer {
          0%   { background-position: 100% 0; }
          50%  { background-position: 0% 0; }
          100% { background-position: 100% 0; }
        }
        @keyframes fake-shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-6px); }
          80%       { transform: translateX(6px); }
        }
        .fade-up-1 { animation: fade-up 0.5s ease 0.2s both; }
        .fade-up-2 { animation: fade-up 0.5s ease 0.4s both; }
        .fade-up-3 { animation: fade-up 0.5s ease 0.6s both; }
        .fade-up-4 { animation: fade-up 0.5s ease 0.8s both; }
        .fade-up-5 { animation: fade-up 0.5s ease 1.0s both; }
        .app-btn { transition: transform 0.15s, opacity 0.15s; }
        .app-btn:hover { transform: translateY(-2px); opacity: 0.85; }
        .app-btn:active { transform: scale(0.96); }
        .details-btn { transition: all 0.2s; }
        .details-btn:hover { background: rgba(255,255,255,0.1) !important; }
      `}</style>

      {/* Background blobs */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        overflow: "hidden"
      }}>
        {status === "authentic" && (
          <>
            <div style={{
              position: "absolute", top: "-20%", left: "-20%",
              width: "60vw", height: "60vw", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(0,255,180,0.07) 0%, transparent 70%)"
            }} />
            <div style={{
              position: "absolute", bottom: "-20%", right: "-20%",
              width: "50vw", height: "50vw", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(0,200,255,0.06) 0%, transparent 70%)"
            }} />
          </>
        )}
        {status === "fake" && (
          <div style={{
            position: "absolute", top: "-20%", left: "-20%",
            width: "80vw", height: "80vw", borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,50,50,0.08) 0%, transparent 70%)"
          }} />
        )}
      </div>

      <div style={{
        position: "relative", zIndex: 1,
        maxWidth: 430, margin: "0 auto",
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        padding: "0 0 40px"
      }}>

        {/* ─── LOADING ─── */}
        {status === "loading" && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 20
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              border: "3px solid rgba(0,255,180,0.15)",
              borderTop: "3px solid #00ffb4",
              animation: "spin 0.8s linear infinite"
            }} />
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, letterSpacing: "0.1em" }}>
              VERIFYING TAG
            </p>
            {tagId && (
              <p style={{
                fontFamily: "monospace", fontSize: 12,
                color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em"
              }}>
                {tagId}
              </p>
            )}
          </div>
        )}

        {/* ─── AUTHENTIC ─── */}
        {status === "authentic" && (
          <>
            {/* Header space */}
            <div style={{ height: 48 }} />

            {/* Shield + Product image */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 0 }}>
              <div style={{ position: "relative", width: 200, height: 200 }}>
                {/* Pulse rings */}
                <div style={{
                  position: "absolute", inset: -20, borderRadius: "50%",
                  border: "1.5px solid rgba(0,255,180,0.2)",
                  animation: "pulse-ring 2.5s ease-in-out infinite"
                }} />
                <div style={{
                  position: "absolute", inset: -40, borderRadius: "50%",
                  border: "1px solid rgba(0,255,180,0.1)",
                  animation: "pulse-ring 2.5s ease-in-out infinite 0.4s"
                }} />

                {/* Product image or shield */}
                <div style={{
                  width: 200, height: 200, borderRadius: "50%",
                  background: productImage
                    ? "transparent"
                    : "linear-gradient(135deg, #0a2a1f 0%, #0d3d2c 100%)",
                  border: "2px solid rgba(0,255,180,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden",
                  animation: "shield-in 0.6s cubic-bezier(0.34,1.56,0.64,1) both, glow-pulse 3s ease-in-out infinite 0.6s"
                }}>
                  {productImage ? (
                    <img src={productImage} alt={productName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : (
                    <svg width="80" height="90" viewBox="0 0 80 90" fill="none">
                      <path d="M40 4L8 16V42C8 61 24 77 40 84C56 77 72 61 72 42V16L40 4Z"
                        fill="rgba(0,255,180,0.12)" stroke="#00ffb4" strokeWidth="2"
                        style={{ animation: "shield-in 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }}
                      />
                      <polyline points="24,46 34,56 56,34" fill="none"
                        stroke="#00ffb4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                        strokeDasharray="100" strokeDashoffset="100"
                        style={{ animation: "check-draw 0.5s ease 0.5s forwards" }}
                      />
                    </svg>
                  )}
                </div>

                {/* Verified badge */}
                <div style={{
                  position: "absolute", bottom: 8, right: 8,
                  width: 44, height: 44, borderRadius: "50%",
                  background: "#00ffb4",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  animation: "shield-in 0.4s ease 0.7s both"
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <polyline points="4,13 9,18 20,6" stroke="#090e1a"
                      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>

            {/* AUTHENTIC text */}
            <div className="fade-up-1" style={{ textAlign: "center", padding: "24px 24px 0" }}>
              <div style={{
                display: "inline-block",
                background: "linear-gradient(90deg, rgba(0,255,180,0.15), rgba(0,200,255,0.15))",
                border: "1px solid rgba(0,255,180,0.3)",
                borderRadius: 100, padding: "4px 16px",
                marginBottom: 12
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.2em",
                  color: "#00ffb4", textTransform: "uppercase"
                }}>
                  JepTags Verified
                </span>
              </div>

              <h1 style={{
                fontFamily: "'Bebas Neue', 'Syne', sans-serif",
                fontSize: "clamp(52px, 18vw, 72px)",
                fontWeight: 400,
                letterSpacing: "0.08em",
                margin: "0 0 4px",
                lineHeight: 1,
                background: "linear-gradient(135deg, #00ffb4 0%, #00d4ff 60%, #00ffb4 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundSize: "200% 100%",
                animation: "shimmer 3s ease-in-out infinite",
                width: "100%",
                textAlign: "center",
                display: "block",
              }}>
                AUTHENTIC
              </h1>
              <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, letterSpacing: "0.05em", margin: 0 }}>
                This product is genuine and verified
              </p>
            </div>

            {/* Product card */}
            <div className="fade-up-2" style={{ margin: "20px 20px 0" }}>
              <div style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20, padding: "20px"
              }}>
                <p style={{
                  fontSize: 18, fontWeight: 600, margin: "0 0 4px",
                  lineHeight: 1.3
                }}>
                  {productName}
                </p>
                {productBrand && (
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 16px" }}>
                    by {productBrand}
                  </p>
                )}

                {/* Stats row */}
                <div style={{ display: "flex", gap: 12 }}>
                  {[
                    { label: "TAG ID", value: tagId.length > 10 ? tagId.slice(0, 10) + "…" : tagId },
                    { label: "SCANS", value: String(scanCount) },
                    { label: "STATUS", value: "✓ Active" },
                  ].map((item) => (
                    <div key={item.label} style={{
                      flex: 1, background: "rgba(0,0,0,0.2)",
                      borderRadius: 12, padding: "10px 8px", textAlign: "center"
                    }}>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", marginBottom: 4 }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* App download CTA */}
            <div className="fade-up-3" style={{ margin: "16px 20px 0" }}>
              <div style={{
                background: "linear-gradient(135deg, rgba(0,255,180,0.08) 0%, rgba(0,200,255,0.08) 100%)",
                border: "1px solid rgba(0,255,180,0.2)",
                borderRadius: 20, padding: "20px"
              }}>
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontWeight: 600, fontSize: 15, margin: "0 0 4px" }}>
                    🎁 Unlock Full Access
                  </p>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", margin: 0, lineHeight: 1.5 }}>
                    View supply chain · Earn rewards · Join lotteries · Full traceability
                  </p>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  {/* App Store */}
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                    className="app-btn"
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      gap: 8, background: "#fff", borderRadius: 12,
                      padding: "10px 12px", textDecoration: "none"
                    }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#000">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 9, color: "#000", letterSpacing: "0.05em" }}>Download on the</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#000", lineHeight: 1 }}>App Store</div>
                    </div>
                  </a>

                  {/* Google Play */}
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                    className="app-btn"
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      gap: 8, background: "#fff", borderRadius: 12,
                      padding: "10px 12px", textDecoration: "none"
                    }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3.18 23.76a2 2 0 0 1-1-.83V1.07a2 2 0 0 1 1-.83l11.65 11.76L3.18 23.76z" fill="#4CAF50"/>
                      <path d="M20.07 13.76 16.8 15.6l-3.27-3.3 3.27-3.3 3.27 1.84a1.5 1.5 0 0 1 0 2.92z" fill="#FFD600"/>
                      <path d="M3.18 23.76 13.53 13 16.8 16.3 3.65 24a2 2 0 0 1-.47-.24z" fill="#F44336"/>
                      <path d="M3.18 1.07a2 2 0 0 1 .47-.24L16.8 8.7l-3.27 3.3L3.18 1.07z" fill="#2196F3"/>
                    </svg>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 9, color: "#000", letterSpacing: "0.05em" }}>Get it on</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#000", lineHeight: 1 }}>Google Play</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* See more details toggle */}
            <div className="fade-up-4" style={{ margin: "12px 20px 0", textAlign: "center" }}>
              <button
                className="details-btn"
                onClick={() => setShowDetails(!showDetails)}
                style={{
                  width: "100%", background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 14, padding: "14px", cursor: "pointer", color: "#fff",
                  fontSize: 13, fontWeight: 500, display: "flex",
                  alignItems: "center", justifyContent: "center", gap: 8
                }}>
                {showDetails ? "Hide Details ↑" : "See Full Details ↓"}
              </button>
            </div>

            {/* Expanded details */}
            {showDetails && (
              <div className="fade-up-4" style={{ margin: "12px 20px 0" }}>
                <div style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 16, padding: "16px"
                }}>
                  {[
                    { label: "Tag ID", value: tagId },
                    { label: "Product", value: productName },
                    { label: "Brand", value: productBrand || "—" },
                    { label: "Category", value: productCategory || "—" },
                    { label: "Batch", value: batchId || "—" },
                    { label: "Total Scans", value: String(scanCount) },
                    { label: "GS1 Verified", value: "✅ Yes" },
                  ].map((row) => (
                    <div key={row.label} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "8px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)"
                    }}>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>{row.label}</span>
                      <span style={{
                        fontSize: 12, fontWeight: 500, maxWidth: "60%",
                        textAlign: "right", wordBreak: "break-all"
                      }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="fade-up-5" style={{
              marginTop: 24, textAlign: "center", padding: "0 20px"
            }}>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: "0.05em" }}>
                Powered by JepTags Authenticity Platform
              </p>
            </div>
          </>
        )}

        {/* ─── FAKE / NOT AUTHENTIC ─── */}
        {status === "fake" && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "40px 24px", textAlign: "center"
          }}>
            <div style={{
              width: 120, height: 120, borderRadius: "50%",
              background: "rgba(255,50,50,0.12)",
              border: "2px solid rgba(255,50,50,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 24,
              animation: "fake-shake 0.5s ease 0.3s both"
            }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#ff3232" strokeWidth="2"/>
                <line x1="8" y1="8" x2="16" y2="16" stroke="#ff3232" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="16" y1="8" x2="8" y2="16" stroke="#ff3232" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <h1 style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 36, fontWeight: 800, color: "#ff3232",
              margin: "0 0 8px", letterSpacing: "-0.02em"
            }}>
              WARNING
            </h1>
            <p style={{ fontSize: 16, fontWeight: 500, margin: "0 0 8px" }}>
              This product could not be verified
            </p>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6, maxWidth: 300 }}>
              This tag was not found in the JepTags system. This may be a counterfeit product. Do not consume or use it.
            </p>
            <div style={{ marginTop: 32 }}>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>Tag ID: {tagId}</p>
            </div>
          </div>
        )}

        {/* ─── ERROR ─── */}
        {status === "error" && (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "40px 24px", textAlign: "center"
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontWeight: 600, margin: "0 0 8px" }}>Verification Failed</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
              {tagId ? `Could not verify tag: ${tagId}` : "No tag ID found in this URL"}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 24, padding: "12px 28px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 12, color: "#fff",
                fontSize: 14, cursor: "pointer"
              }}>
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthenticScanPage;